import React, { useEffect, useRef, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Button, Input, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./modal_purchase_request.css";
import axios from 'axios';
import useSWR, { mutate } from 'swr';

interface DataType {
    key: string;
    prNo: string;
    prDate: string;
    soNo: string;
}

type DataIndex = keyof DataType;
const fetcher = (url: string) => axios.get(url).then(res => res.data);
interface ModalPurchaseRequestProps {
    selectedRowKeys: React.Key[];
    onSelectChange: (selectedRowKeys: React.Key[]) => void;
    orderType: string | null;
    warehouseCode: string | null;
    existingPrNos?: string[];
}

const ModalPurchaseRequest: React.FC<ModalPurchaseRequestProps> = ({ selectedRowKeys, onSelectChange, orderType, warehouseCode, existingPrNos }) => {
    const apiUrl = orderType && warehouseCode ? `${process.env.REACT_APP_BACKEND_URL}/api/po/purchase-requests?order_type=${orderType}&dealer_rep_code=${warehouseCode}` : null;
    const { data: prData, error: prError, isLoading: prIsLoading } = useSWR<DataType[]>(apiUrl, fetcher);
    const { data: usedPrNos } = useSWR<string[]>(`${process.env.REACT_APP_BACKEND_URL}/api/po/used-pr-nos`, fetcher);

    useEffect(() => {
        const eventSource = new EventSource(`${process.env.REACT_APP_BACKEND_URL}/api/po/used-pr-nos/stream`);
        eventSource.onmessage = (event) => {
            const updatedPrNos = JSON.parse(event.data);
            mutate(`${process.env.REACT_APP_BACKEND_URL}/api/po/used-pr-nos`, updatedPrNos, false);
        };
        return () => {
            eventSource.close();
        };
    }, []);

    const isDisabled = (prNo: string) => {
        return (existingPrNos || []).includes(prNo) || (usedPrNos || []).includes(prNo);
    };
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        getCheckboxProps: (record: DataType) => ({
            disabled: isDisabled(record.prNo),
        }),
    };
    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<DataType> = [
        {
            title: 'PR No',
            dataIndex: 'prNo',
            key: 'prNo',
            ...getColumnSearchProps('prNo'),
            sorter: (a, b) => a.prNo.localeCompare(b.prNo),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'PR Date',
            dataIndex: 'prDate',
            key: 'prDate',
            ...getColumnSearchProps('prDate'),
            sorter: (a, b) => new Date(a.prDate).getTime() - new Date(b.prDate).getTime(),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'SO No',
            dataIndex: 'soNo',
            key: 'soNo',
            ...getColumnSearchProps('soNo'),
            sorter: (a, b) => (a.soNo || '').localeCompare(b.soNo || ''),
            sortDirections: ['descend', 'ascend'],
        },
    ];

    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };
    if (prError) return <div>Gagal memuat data. Silakan coba lagi.</div>;
    return (

        <div id="ilqt" style={transformStyle}>
            <div className="gjs-grid-row" id="i8mk">
                <div className="gjs-grid-column" id="i0ah">
                    <Table<DataType>
                        rowSelection={rowSelection}
                        columns={columns}
                        dataSource={prData}
                        loading={prIsLoading}
                        onRow={(record) => ({
                            style: isDisabled(record.prNo)
                                ? { opacity: 0.5, cursor: 'not-allowed' }
                                : {}
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default ModalPurchaseRequest;